import { convertEmptyToNull } from './formatter';

// Feature flag to enable/disable empty string to null transformation
const ENABLE_EMPTY_STRING_TO_NULL_TRANSFORM = true;

/**
 * Recursively transforms empty strings to null in an object
 * Keeps it simple - transforms all string fields that are empty strings
 */
function emptyStringToNullTransformer(
  obj: any,
  maxDepth: number = 5,
  currentDepth: number = 0
): any {
  // Prevent infinite recursion
  if (currentDepth >= maxDepth) {
    return obj;
  }

  // Handle null, undefined, or non-objects
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map((item) =>
      emptyStringToNullTransformer(item, maxDepth, currentDepth + 1)
    );
  }

  // Handle Date objects and other special objects - preserve as-is
  if (obj instanceof Date || obj instanceof File) {
    return obj;
  }

  // Handle regular objects
  const result: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      result[key] = convertEmptyToNull(value);
    } else if (typeof value === 'object' && value !== null) {
      result[key] = emptyStringToNullTransformer(
        value,
        maxDepth,
        currentDepth + 1
      );
    } else {
      result[key] = value;
    }
  }
  return result;
}

export interface ApiOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: { [key: string]: string };
  body?: any;
}

export class ApiError extends Error {
  type: string;
  title: string;
  status: number;
  detail: string;
  message: string;
  errors: { [key: string]: string[] };

  constructor(errorData: any) {
    super(errorData.title);
    this.type = errorData.type;
    this.title = errorData.title;
    this.status = errorData.status;
    this.detail = errorData.detail;
    this.message = errorData.detail ?? errorData.title;
    this.errors = errorData.errors;
  }
}

// Maximum timeout supported
const FRONTEND_TIMEOUT = 60000;

export const callApi = async <T>(
  url: string,
  options: ApiOptions
): Promise<T> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FRONTEND_TIMEOUT);

  // Transform empty strings to null if enabled and body exists
  let transformedBody = options.body;
  if (ENABLE_EMPTY_STRING_TO_NULL_TRANSFORM && options.body) {
    try {
      transformedBody = emptyStringToNullTransformer(options.body);
    } catch (error) {
      // If transformation fails, use original body to not halt operations
      console.warn(
        'Empty string to null transformation failed, using original payload:',
        error
      );
      transformedBody = options.body;
    }
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body: transformedBody ? JSON.stringify(transformedBody) : undefined,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      if (response.status === 401) {
        // Redirect to login page if unauthorized
        window.location.href = '/auth/login';
        throw new Error('Unauthorized');
      }

      if (response.status === 400) {
        const errorData = await response.json();

        // Special case for "Pet not found" error - expected in logged-out flow due to eventual consistency
        // Return empty array instead of throwing error
        if (
          errorData.detail === 'Pet not found' &&
          url.includes('/businessServices')
        ) {
          console.warn(
            "Ignoring 'Pet not found' error - pet may not be fully propagated in the system yet"
          );
          return [] as unknown as T;
        }

        throw new ApiError(errorData);
      }

      throw new ApiError({
        title: response.statusText,
        status: response.status,
        detail: response.statusText,
        message: response.statusText,
      });
    }

    return response.json();
  } catch (error: unknown) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(
        'Request timeout - Operation took longer than 29 seconds'
      );
    }
    throw error;
  }
};
